import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToId } from "../lib/smoothScroll";
import { WHATSAPP } from "../data/content";

const LINKS = [
  { label: "Catalogo", hash: "#catalogo", testId: "nav-catalogo-link" },
  { label: "Servizi & Posa", hash: "#servizi", testId: "nav-servizi-link" },
  { label: "Perché Noi", hash: "#perche-noi", testId: "nav-perche-link" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollToId(hash);
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#181410]/85 border-b border-amber-500/15 shadow-[0_4px_30px_rgba(0,0,0,0.5)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0 })} aria-label="Torna su" className="shrink-0">
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={l.testId}
              onClick={() => go(l.hash)}
              className="text-sm text-stone-300 hover:text-amber-300 transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-whatsapp-btn"
            href={`https://wa.me/${WHATSAPP.phone}?text=${encodeURIComponent(WHATSAPP.message)}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            className="hidden sm:flex w-10 h-10 items-center justify-center rounded-full border border-amber-500/25 text-amber-300 hover:border-amber-400/60 hover:bg-amber-400/10 transition-colors duration-300"
          >
            <MessageCircle className="w-4 h-4" />
          </a>
          <button
            data-testid="nav-quote-btn"
            onClick={() => go("#preventivo")}
            className="hidden sm:inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full bg-amber-400 text-[#181410] hover:bg-amber-300 transition-colors duration-300"
          >
            Richiedi Preventivo
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="lg:hidden w-10 h-10 flex items-center justify-center text-stone-200"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden backdrop-blur-xl bg-[#181410]/95 border-b border-amber-500/15"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.hash}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => go(l.hash)}
                  className="text-left text-lg font-serif text-stone-200 hover:text-amber-300 transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-quote-btn-mobile"
                onClick={() => go("#preventivo")}
                className="mt-2 inline-flex items-center justify-center px-5 py-3 text-sm font-medium rounded-full bg-amber-400 text-[#181410]"
              >
                Richiedi Preventivo
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
